import React from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import File from './file';

import Directory from './directory';

import Actions from '@app/action';
import PreviewHelper from '@app/helper/preview';
import { TransferItemUITypes } from '@app/constant/transfer-item-types';

import Spinner from '@component/spinner';
import Icon from '@component/icon';

import Router from '@module/router';
import { getIsForwardedTransfer } from '@app/transfer-bundle/store/selectors';

// because of the transfer-object mutability
// this class can not extend PureComponent
class ItemList extends React.Component {
  state = {
    hoverNum: null,
  };

  constructor(props) {
    super(props);

    this.deleteMethods = {
      [TransferItemUITypes.file]: props.removeFile,
      [TransferItemUITypes.directory]: props.removeDirectory,
    };
  }

  async onDownload(num, e) {
    e.stopPropagation();
    e.preventDefault();

    const { transfer, errors, onDownload, resetFormErrors } = this.props;

    await onDownload(transfer.items[num].id, e);

    if (errors.transfer) {
      resetFormErrors('transfer');
    }
  }

  /**
   * Updates the hoverNum property in the component state if the browser is not a touch device.
   *
   * @param  {number} num file index
   */
  updateHoverNum(num) {
    if (Browser.supportsTouchEvents) {
      return null;
    }
    this.setState({ hoverNum: num });
  }

  onDeleteActionClick = (event) => {
    const { itemName, itemType } = event.currentTarget.dataset;

    const deleteMethod = this.deleteMethods[itemType];
    if (!deleteMethod) {
      return;
    }

    deleteMethod(itemName);
  };

  renderDeleteAction(itemName, itemType) {
    return (
      <div
        className="filelist__action filelist__action--delete"
        data-item-name={itemName}
        data-item-type={itemType}
        onClick={this.onDeleteActionClick}
      >
        <Icon type="Remove" />
      </div>
    );
  }

  renderDownloadAction(num, itemName) {
    const item = this.props.transfer.items.find(
      (item) => item.name === itemName
    );
    if (!item || this.props.showPending) {
      return null;
    }

    const { file } = item;

    // Should we render a spinner for this file?
    if (file.state && file.state.download === 'downloading') {
      return (
        <div
          className="filelist__spinner"
          onMouseLeave={() => this.setState({ hoverNum: null })}
        >
          <Spinner
            rotate={true}
            percentage={25}
            hideText
            circumference={24}
            stroke={2}
          />
        </div>
      );
    }

    // Or is this a delete action?
    if (this.props.removableItems && !this.props.isForwardedTransfer) {
      return this.renderDeleteAction(file.name, TransferItemUITypes.file);
    }

    // Maybe it's downloadable?
    if (this.props.onDownload) {
      let icon = <Icon type="DownloadSmall" />;
      const className = ['filelist__action'];

      if (
        file.state &&
        file.state.download === 'done' &&
        this.state.hoverNum !== num
      ) {
        icon = <Icon type="CheckCircle" />;
        className.push('filelist__action--downloaded');
      }

      return (
        <div
          className={className.join(' ')}
          onClick={(e) => this.onDownload(num, e)}
          onMouseOver={() => this.updateHoverNum(num)}
          onMouseLeave={() => this.updateHoverNum(null)}
        >
          {icon}
        </div>
      );
    }

    return null;
  }

  renderPreviewAction() {
    return (
      <div className="filelist__action">
        <Icon type="SearchCircle" />
      </div>
    );
  }

  renderDirectoryActions(directoryName) {
    const { isForwardedTransfer, removableItems } = this.props;

    if (!removableItems || isForwardedTransfer) {
      return null;
    }

    return this.renderDeleteAction(
      directoryName,
      TransferItemUITypes.directory
    );
  }

  renderTransferItem = (item, num) => {
    const {
      id,
      secret,
      recipient,
      maxFiles,
      file_id,
      showPending,
      route,
    } = this.props;
    if (maxFiles && num >= maxFiles) {
      return null;
    }

    if (item.uiType === TransferItemUITypes.directory) {
      return (
        <Directory
          item={item}
          actions={this.renderDirectoryActions(item.name)}
        />
      );
    }

    let onClick = null;
    if (!showPending && item.previewable) {
      onClick = () =>
        Router.navigateTo(
          PreviewHelper.getPreviewUrl({
            transferId: id,
            secret,
            recipient,
            fileId: item.id,
          })
        );
    }

    const active = item.id === file_id && !!route.path.match(/download/);
    let fileAction = null;

    if (item.previewable && !Browser.isMobile) {
      fileAction = this.renderPreviewAction(item.id);
    } else {
      fileAction = this.renderDownloadAction(num, item.name);
    }

    return (
      <File
        onClick={onClick}
        active={active}
        file={item}
        altText={this.props.altText}
        showPending={this.props.showPending}
        action={fileAction}
      />
    );
  };

  // Render the list of items.
  render() {
    const { transfer } = this.props;
    const classNames = ['filelist'];

    if (this.props.showPending) {
      classNames.push('filelist--pending');
    }

    if (this.props.previewable) {
      classNames.push('filelist--support-previews');
    }

    if (!transfer.items || !transfer.items.length) {
      return null;
    }

    return (
      <ul className={classNames.join(' ')}>
        {transfer.itemsTree.items.map((item, index) => (
          <li key={item.name}>{this.renderTransferItem(item, index)}</li>
        ))}
      </ul>
    );
  }
}

ItemList.propTypes = {
  onDownload: PropTypes.func,
  onPreview: PropTypes.func,
  altText: PropTypes.string,
  showPending: PropTypes.bool,
  transfer: PropTypes.object,
  maxFiles: PropTypes.number,
  previewable: PropTypes.bool,
  id: PropTypes.string,
  secret: PropTypes.string,
  recipient: PropTypes.string,
  file_id: PropTypes.string,
  errors: PropTypes.object,
  resetFormErrors: PropTypes.func,
  route: PropTypes.object,
  removeFile: PropTypes.func,
  removeDirectory: PropTypes.func,
  removableItems: PropTypes.bool,
  isForwardedTransfer: PropTypes.bool,
};

ItemList.defaultProps = {
  showPending: false,
  maxFiles: 0,
  previewable: false,
};

const mapStateToProps = (state) => ({
  errors: state.errors,
  route: state.route,
  isForwardedTransfer: getIsForwardedTransfer(state),
});

const mapDispatchToProps = {
  resetFormErrors: Actions.Errors.resetFormErrors,
  removeFile: Actions.Transfer.removeFile,
  removeDirectory: Actions.Transfer.removeDirectory,
};

export { ItemList };

// the reason for `{ pure: true }` is the same as for having `React.Component`
// instead of `React.PureComponent` — transfer-object mutability
export default connect(mapStateToProps, mapDispatchToProps, null, {
  pure: false,
})(ItemList);
